import "./global.css";

import { initDisplayLanguage } from "@/lib/display-language";
import { initTheme } from "@/lib/theme";

initTheme();
initDisplayLanguage();

import { I18nProvider } from "@lingui/react";
import { i18n } from "@/lib/i18n";
import { Toaster } from "@/shadcn/toaster";
import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/shadcn/sonner";
import { TooltipProvider } from "@/shadcn/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { AuthProvider } from "@/auth/AuthProvider";
import { PrivateRoute } from "@/auth/PrivateRoute";
import { AppRouter } from "@/routes/AppRouter";
import { PublicRouter } from "./pages/public/PublicRouter";
import { VoiceRouter } from "./pages/public/voice/VoiceRouter";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <I18nProvider i18n={i18n}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Root redirects into the monitoring dashboard */}
              <Route
                path="/"
                element={<Navigate to="/monitoring/dashboard" replace />}
              />

              {/* The /public/ prefix is historical: everything under it
                requires a session, and its pages are gated per user by
                feature flags. Do not move outside <PrivateRoute>. */}
              <Route element={<PrivateRoute />}>
                <Route path="/public/*" element={<PublicRouter />} />
              </Route>

              {/* Caller-facing voice-auth flow — short SMS-friendly URLs.
                Mixes public (magic-link token) + session-authed sub-routes;
                gating is handled inside VoiceRouter. */}
              <Route path="/voice/*" element={<VoiceRouter />} />

              {/* Authenticated app — single shell, flat router */}
              <Route element={<PrivateRoute />}>
                <Route path="/*" element={<AppRouter />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </I18nProvider>
  </QueryClientProvider>
);

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");
createRoot(rootElement).render(<App />);
